<template>
  <div class="d-flex flex-column">
    <InvoiceHeader :invoice="job.invoice" @refresh-invoice="refreshInvoice" />
    <div v-if="job.invoice">
      <Invoice
        class="ssui-invoice"
        :invoice="job.invoice"
        :customizations="customizations"
        :equipment-types-enabled="equipmentTypesEnabled"
        :tenant-timezone="tenantTimezone"
        @edit-transaction-note-click="handleEditNoteClick"
        @edit-transaction-reference-id-click="handleEditReferenceIdClick"
        @equipment-request-click="toggleModal('captureModal')"
      >
        <template #paymentSchedule>
          <div v-if="spbVisible" class="spb-row" data-cy="spb-toggle-row">
            <Checkbox
              :modelValue="spbCheckboxValue"
              :disabled="spbPlanLocked"
              class="spb-row__checkbox"
              data-cy="spb-checkbox"
              @update:modelValue="toggleSpb"
            />
            <span class="spb-row__label">Structured Payment Billing</span>
            <span
              v-if="spbPlanLocked && spbEnabled"
              class="spb-row__status spb-row__status--locked"
              data-cy="spb-locked"
            >
              <i class="fas fa-lock mr-1" />Plan locked
            </span>
            <span
              v-else-if="spbEnabled && spbPlanStale"
              class="spb-row__status spb-row__status--stale"
              data-cy="spb-stale"
            >
              <i class="fas fa-exclamation-triangle mr-1" />Plan needs update
            </span>
            <span
              v-else-if="spbEnabled"
              class="spb-row__status"
              data-cy="spb-summary"
            >
              <i class="fas fa-check mr-1" />Plan configured
            </span>
            <a
              v-if="spbEnabled"
              href="#"
              class="spb-row__action"
              data-cy="spb-edit-btn"
              @click.prevent="openPaymentScheduleModal"
            >
              {{ spbPlanLocked ? "View" : "Edit" }}
            </a>
            <a
              v-else-if="
                spbConfigureEnabled &&
                !jobPaymentScheduleLoading &&
                !spbPlanLocked
              "
              href="#"
              class="spb-row__action"
              data-cy="spb-configure-btn"
              @click.prevent="openPaymentScheduleModal"
            >
              Configure
            </a>
          </div>
          <div
            v-if="spbEnabled && spbPlanStale && !spbPlanLocked"
            class="spb-stale-banner"
            data-cy="spb-stale-banner"
          >
            <i class="fas fa-exclamation-triangle mr-2" />
            <div>
              <strong>Payment schedule is out of date.</strong>
              The dollar-based plan total no longer matches the invoice balance.
              Reconfigure the schedule before accepting any quote on this job —
              approval will be blocked until the plan is updated.
            </div>
          </div>
          <PaymentScheduleSection
            v-if="jobPaymentSchedule"
            :schedule="jobPaymentSchedule"
          />
        </template>
      </Invoice>
      <ConfigurePaymentScheduleModal
        v-if="paymentScheduleModal"
        :job-id="job.id"
        :invoice-balance-cents="invoiceBalanceCents"
        :existing-plan="jobPaymentSchedule"
        :plan-locked="spbPlanLocked"
        @hide-modal="toggleModal('paymentScheduleModal')"
        @saved="handleJobPaymentScheduleSaved"
        @needs-refresh="handlePaymentScheduleNeedsRefresh"
      />
      <EquipmentCaptureModal
        v-if="captureModal"
        :invoice="job.invoice"
        :address="job.address"
        :equipment="customerEquipment"
        @hide-modal="toggleModal('captureModal')"
        @refresh-invoice="refreshInvoice"
      />
      <EditTransactionNotesModal
        v-if="editNotesModal"
        :transaction="selectedTransaction"
        @hide-modal="clearNotesModal"
      />
      <TransactionReferenceIdModal
        v-if="editReferenceIdModal"
        :transaction="selectedTransaction"
        @hide-modal="clearReferenceIdModal"
      />
    </div>
    <div v-else class="no-invoice">
      <i class="fad fa-question-circle"></i>
      No Invoice Available
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { EventBus } from "@/lib/event_bus.js";

import Checkbox from "@/components/Checkbox.vue";
import EquipmentCaptureModal from "@/Equipment/EquipmentCaptureModal.vue";
import InvoiceHeader from "@/Invoice/InvoiceHeader.vue";
import EditTransactionNotesModal from "@/Job/EditTransactionNotesModal.vue";
import ConfigurePaymentScheduleModal from "@/Job/QuoteBuilder/ConfigurePaymentScheduleModal.vue";
import TransactionReferenceIdModal from "@/Job/TransactionReferenceIdModal.vue";
import PaymentScheduleSection from "@/serviceshift-ui/components/QuoteInvoice/PaymentScheduleSection.vue";

export default {
  components: {
    TransactionReferenceIdModal,
    InvoiceHeader,
    EditTransactionNotesModal,
    EquipmentCaptureModal,
    Checkbox,
    ConfigurePaymentScheduleModal,
    PaymentScheduleSection
  },
  data() {
    return {
      editNotesModal: false,
      editReferenceIdModal: false,
      selectedTransaction: null,
      captureModal: false,
      paymentScheduleModal: false,
      jobPaymentSchedule: null,
      jobPaymentInvoice: null,
      jobPaymentScheduleLoading: false,
      progressBillingConfig: null,
      spbWanted: false
    };
  },
  computed: {
    ...mapGetters(["featureEnabledForTenant"]),
    ...mapState({
      job: "job",
      customerEquipment: "customerEquipment",
      customizations: ({ tenantCustomizations, tenant }) => {
        if (!tenant || !tenantCustomizations) {
          return null;
        }
        return {
          ...tenantCustomizations,
          Tenant: { ...tenant }
        };
      },
      equipmentTypesEnabled({ tenant }) {
        if (!tenant) return false;
        return tenant.feature_flags.equipment_types;
      },
      tenantTimezone({ tenant }) {
        if (!tenant) return "";
        return tenant.timezone;
      }
    }),
    invoiceBalanceCents() {
      return this.job?.invoice?.financials?.balance_cents || 0;
    },
    progressBillingEnabled() {
      return (
        this.featureEnabledForTenant("progress_billing") &&
        Boolean(this.progressBillingConfig?.enabled)
      );
    },
    spbVisible() {
      if (!this.progressBillingEnabled) return false;
      if (!this.job?.id) return false;
      if (!this.job?.invoice) return false;
      return true;
    },
    spbCheckboxValue() {
      return Boolean(this.jobPaymentSchedule) || this.spbWanted;
    },
    spbConfigureEnabled() {
      return this.spbWanted && !this.jobPaymentSchedule;
    },
    spbEnabled() {
      return Boolean(this.jobPaymentSchedule);
    },
    spbPlanLocked() {
      return Boolean(this.job?.invoice?.finalized);
    },
    spbPlanStale() {
      const plan = this.jobPaymentSchedule;
      if (!plan || plan.split_mode !== "amount") return false;
      const planTotal = (plan.installments || []).reduce(
        (sum, i) => sum + Number(i.value || 0),
        0
      );
      const balanceCents = this.job?.invoice?.financials?.balance_cents ?? 0;
      return planTotal !== balanceCents;
    }
  },
  watch: {
    "job.id": {
      immediate: false,
      handler(newId) {
        if (newId) this.loadJobPaymentSchedule();
      }
    }
  },
  async created() {
    EventBus.$on("refresh-invoice", this.refreshInvoice);
    await this.loadProgressBillingConfig();
    await this.loadJobPaymentSchedule();
  },
  beforeUnmount() {
    EventBus.$off("refresh-invoice", this.refreshInvoice);
  },
  methods: {
    ...mapActions([
      "fetchJob",
      "fetchJobPaymentSchedule",
      "deleteJobPaymentSchedule",
      "fetchProgressBillingConfig"
    ]),
    toggleModal(modal) {
      this[modal] = !this[modal];
    },
    handleEditNoteClick(transaction) {
      this.selectedTransaction = transaction;
      this.editNotesModal = true;
    },
    handleEditReferenceIdClick(transaction) {
      this.selectedTransaction = transaction;
      this.editReferenceIdModal = true;
    },
    clearNotesModal() {
      this.editNotesModal = false;
      this.selectedTransaction = null;
    },
    clearReferenceIdModal() {
      this.editReferenceIdModal = false;
      this.selectedTransaction = null;
    },
    refreshInvoice() {
      this.fetchJob({ jobId: this.job.id });
    },

    async loadProgressBillingConfig() {
      if (!this.featureEnabledForTenant("progress_billing")) return;
      try {
        this.progressBillingConfig = await this.fetchProgressBillingConfig();
      } catch {
        this.progressBillingConfig = null;
      }
    },

    async loadJobPaymentSchedule() {
      if (!this.progressBillingEnabled) return;
      if (!this.job?.id) return;
      this.jobPaymentScheduleLoading = true;
      try {
        const response = await this.fetchJobPaymentSchedule(this.job.id);
        this.applyScheduleResponse(response);
      } finally {
        this.jobPaymentScheduleLoading = false;
      }
    },

    applyScheduleResponse(response) {
      if (!response) {
        this.jobPaymentSchedule = null;
        this.jobPaymentInvoice = null;
        return;
      }
      this.jobPaymentSchedule = response.schedule ?? null;
      this.jobPaymentInvoice = response.invoice ?? null;
    },

    async openPaymentScheduleModal() {
      if (this.jobPaymentScheduleLoading || this.paymentScheduleModal) return;
      await this.loadJobPaymentSchedule();
      this.paymentScheduleModal = true;
    },

    async toggleSpb() {
      if (this.jobPaymentScheduleLoading) return;
      if (this.spbPlanLocked) return;
      if (this.spbEnabled) {
        if (!window.confirm("Remove the payment schedule for this job?"))
          return;
        this.jobPaymentScheduleLoading = true;
        try {
          await this.deleteJobPaymentSchedule(this.job.id);
          this.jobPaymentSchedule = null;
          this.spbWanted = false;
        } finally {
          this.jobPaymentScheduleLoading = false;
        }
      } else if (this.spbWanted) {
        this.spbWanted = false;
      } else {
        this.spbWanted = true;
      }
    },

    handleJobPaymentScheduleSaved(response) {
      this.applyScheduleResponse(response);
      this.spbWanted = false;
    },

    async handlePaymentScheduleNeedsRefresh() {
      this.refreshInvoice();
      await this.loadJobPaymentSchedule();
    }
  }
};
</script>

<style lang="scss" scoped>
.no-invoice {
  color: var(--gray);
  font-size: 1.1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 15px;
  i {
    font-size: 4rem;
    margin-bottom: 10px;
  }
}

:deep(.invoice-body) {
  .table {
    margin-bottom: 0;
    border-radius: 4px 4px 0 0;
  }
  .totals {
    box-shadow: var(--box-shadow);
  }
}

:deep(.table td) {
  border-top: none;
  vertical-align: baseline !important;
}

.financing-option {
  display: flex;
  align-items: center;
  padding-left: 12px;
  border-top: 1px solid;
  background: #f4f5f8;
}

.spb-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 12px;
  gap: 12px;
  margin: 0;
  background: #f4f5f8;
  border-top: 1px solid var(--primary-light-accent-color, #cbd5e1);
}

.spb-row__checkbox {
  margin: 0;
}

.spb-row__label {
  font-weight: 600;
}

.spb-row__status {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  background-color: #d1fae5;
  color: #065f46;
  font-size: 0.85rem;
  font-weight: 500;

  &--stale {
    background-color: #fef3c7;
    color: #92400e;
  }

  &--locked {
    background-color: #e5e7eb;
    color: #374151;
  }
}

.spb-stale-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 0.5rem 0 0.75rem;
  padding: 0.75rem 1rem;
  border-left: 4px solid #f59e0b;
  background-color: #fffbeb;
  color: #78350f;
  font-size: 0.9rem;
  line-height: 1.4;

  i {
    margin-top: 2px;
    color: #b45309;
  }

  strong {
    display: block;
    margin-bottom: 0.15rem;
  }
}

.spb-row__action {
  padding: 0;
  background: transparent;
  border: 0;
  color: var(--primary-color);
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }
}
</style>
